import { ReactNode, useMemo, useRef } from "react";
import cube from "@cubejs-client/core";
import { CubeProvider } from "@cubejs-client/react";
import jwtDecode from "jwt-decode";
import { serverGql } from "__generated__/serverGql";
import serverClient from "utils/apollo/serverClient";
import { assertPresence } from "utils/assertions";
import { useCurrentBrandId } from "utils/currentUserHooks";
import { useOnBrandChange } from "utils/routeParamHooks";

const tokenMutation = serverGql(/* ServerGraphQL */ `
  mutation GetCubeToken($brandId: UUID!) {
    getCubeToken(brandId: $brandId) {
      token
    }
  }
`);

type JwtPayload = {
  exp: number;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  brand_id: string;
};

type Props = {
  children: ReactNode;
};

function isTokenValid(token: string, requiredBrandId: string): boolean {
  try {
    const decoded = jwtDecode<JwtPayload>(token);
    const currentTime = Math.floor(Date.now() / 1000);

    // Check if token is for the correct brand and not expired (with 5 minute buffer)
    return (
      decoded.brand_id === requiredBrandId && currentTime < decoded.exp - 300
    );
  } catch {
    // If we can't decode the token, consider it invalid
    return false;
  }
}

export default function CubeAuthProvider({ children }: Props) {
  const brandId = useCurrentBrandId();
  const tokenCacheRef = useRef<string | null>(null);

  useOnBrandChange(() => {
    tokenCacheRef.current = null;
  });

  const cubeApi = useMemo(
    () =>
      cube(
        async () => {
          assertPresence(brandId, "Brand ID is required for Cube.js token");

          // Check if we have a valid cached token for this brand
          const cachedToken = tokenCacheRef.current;
          if (cachedToken && isTokenValid(cachedToken, brandId)) {
            return cachedToken;
          }

          // Fetch new token if cache is invalid or expired
          const result = await serverClient.mutate({
            mutation: tokenMutation,
            variables: { brandId },
            fetchPolicy: "no-cache",
          });

          const token = result.data?.getCubeToken?.token;
          assertPresence(token, "Received empty token in response!");

          // Cache the new token
          tokenCacheRef.current = token;

          return token;
        },
        {
          apiUrl: "/cube-api/v1",
        },
      ),
    [brandId],
  );

  return <CubeProvider cubeApi={cubeApi}>{children}</CubeProvider>;
}
